import { createClient } from '@supabase/supabase-js';
import type { User, Question, UserStats, PracticeSession, UserQuestionProgress } from '../types';
import { buildSiteUrl } from './site-url';
import { getAcquisitionFields } from './utm';
import { localDateString } from './dates';

// Environment variables - these should be set in .env file
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// ============================================
// Database Types for Supabase
// ============================================

export interface Database {
  public: {
    Tables: {
      users: {
        Row: User;
        Insert: Omit<User, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<User, 'id'>>;
      };
      questions: {
        Row: Question;
        Insert: Omit<Question, 'id' | 'created_at' | 'updated_at' | 'times_shown' | 'times_correct'>;
        Update: Partial<Omit<Question, 'id'>>;
      };
      user_stats: {
        Row: UserStats;
        Insert: Omit<UserStats, 'user_id'>;
        Update: Partial<UserStats>;
      };
      practice_sessions: {
        Row: PracticeSession;
        Insert: Omit<PracticeSession, 'id'>;
        Update: Partial<PracticeSession>;
      };
      user_question_progress: {
        Row: UserQuestionProgress;
        Insert: Omit<UserQuestionProgress, 'id'>;
        Update: Partial<UserQuestionProgress>;
      };
    };
  };
}

// ============================================
// Auth Helpers
// ============================================

export const auth = {
  // Sign up with email
  async signUp(email: string, password: string, fullName: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    if (error) throw error;
    return data;
  },

  // Sign in with email
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  },

  // Sign in with Google OAuth
  // On native (iOS/Android), Google blocks OAuth in embedded WebViews.
  // Route through a Safari View Controller via @capacitor/browser instead.
  // The custom-scheme callback (com.pulsemd.app://auth/callback) is
  // handled by the deep-link listener registered in App.tsx (Task 6).
  async signInWithGoogle() {
    const { isNative } = await import('./native/platform');
    if (isNative()) {
      const { Browser } = await import('@capacitor/browser');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'com.pulsemd.app://auth/callback',
          skipBrowserRedirect: true,
        },
      });
      if (error) throw error;
      if (!data?.url) throw new Error('OAuth URL not returned');
      await Browser.open({ url: data.url, presentationStyle: 'popover' });
      return data;
    }
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // Use SITE_URL (env-driven) — NOT window.location.origin —
        // so the OAuth callback always returns to the right domain
        // regardless of which environment the user started from.
        redirectTo: buildSiteUrl('/auth/callback'),
      },
    });
    if (error) throw error;
    return data;
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  // Get current user
  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  // Get session
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },

  // Reset password
  async resetPassword(email: string) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      // SITE_URL (env-driven) — see src/lib/site-url.ts. Without this,
      // password-reset emails triggered from localhost dev would link
      // back to localhost. The redirectTo URL must also be in the
      // Supabase project's "Redirect URLs" allowlist (Auth settings).
      redirectTo: buildSiteUrl('/auth/reset-password'),
    });
    if (error) throw error;
    return data;
  },

  // Update password
  async updatePassword(newPassword: string) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
    return data;
  },

  // Listen to auth changes
  onAuthStateChange(callback: (event: string, session: unknown) => void) {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// ============================================
// Database Helpers
// ============================================

export const db = {
  // Users
  users: {
    async get(userId: string) {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) throw error;
      return data as User;
    },

    // Non-throwing lookup for get-or-create paths. Uses maybeSingle() so a
    // missing row returns null with an HTTP 200 — .single() would make
    // PostgREST answer 406, which the browser logs as a red console error on
    // every first signup (benign, but noisy). Returns null only when the row
    // is genuinely absent; real errors (network, RLS) still throw.
    async tryGet(userId: string) {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      return (data as User | null) ?? null;
    },

    async create(user: Omit<User, 'created_at' | 'updated_at'>) {
      // Ad/UTM attribution captured on the landing page. Merged here so every
      // signup path (email + Google OAuth) is attributed in one place. Explicit
      // caller fields win, so this only fills the acquisition columns.
      const acquisition = getAcquisitionFields();

      const { data, error } = await supabase
        .from('users')
        .insert({ ...acquisition, ...user })
        .select()
        .single();
      if (!error) return data as User;
      // Idempotent: a handle_new_user trigger (or a prior attempt) may have
      // already created this profile. On a unique-key conflict, return the
      // existing row instead of throwing — respecting any trigger-set values
      // (e.g. an invited 'creator' role) rather than overwriting them.
      if (error.code === '23505') {
        const { data: existing, error: getErr } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();
        if (getErr || !existing) throw error;
        // The trigger created the row without attribution. Backfill the
        // acquisition columns that we captured and the row doesn't already have
        // (first-touch wins — never overwrite an existing source).
        const patch = Object.fromEntries(
          Object.entries(acquisition).filter(([k, v]) => v && !(existing as Record<string, unknown>)[k])
        );
        if (Object.keys(patch).length > 0) {
          const { data: updated } = await supabase
            .from('users')
            .update(patch)
            .eq('id', user.id)
            .select()
            .single();
          if (updated) return updated as User;
        }
        return existing as User;
      }
      throw error;
    },

    async update(userId: string, updates: Partial<User>) {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();
      if (error) throw error;
      return data as User;
    },

    async getStats(userId: string) {
      const { data, error } = await supabase
        .from('user_stats')
        .select('*')
        .eq('user_id', userId)
        .single();
      if (error) throw error;
      return data as UserStats;
    },
  },

  // Questions
  questions: {
    /**
     * Pick one interview question for the given user using the
     * select_interview_question() RPC. Returns null if the eligible
     * bank is empty (user has answered every interview Q at "strong"
     * within the past 7 days, OR there are no interview questions yet).
     */
    async selectInterview(userId: string, excludeIds: string[] = []): Promise<Question | null> {
      const { data, error } = await supabase.rpc('select_interview_question', {
        p_user_id: userId,
        p_exclude_recent: excludeIds,
      });
      if (error) throw error;
      // RPC returns a single jsonb row (or null when bank is empty).
      if (!data) return null;
      return data as Question;
    },

    /**
     * Questions for a practice/category session, chosen server-side by
     * select_practice_questions() (migration 77): due spaced-repetition reviews
     * (capped at 70%), then never-seen questions at random, then least recently
     * seen; nothing seen in the last 20 hours. Until that migration is applied the
     * RPC is missing, so fall back to the legacy getPublished query.
     */
    async selectPractice(userId: string, limit = 10, category?: string): Promise<Question[]> {
      const { data, error } = await supabase.rpc('select_practice_questions', {
        p_user_id: userId,
        p_limit: limit,
        p_category: category ?? null,
      });
      if (!error) return (data ?? []) as Question[];
      const missing = error.code === 'PGRST202' || /select_practice_questions/.test(error.message ?? '');
      if (!missing) throw error;
      return db.questions.getPublished(limit, category, userId);
    },

    async getPublished(limit = 10, category?: string, excludeUserId?: string) {
      // If we have a userId, exclude questions they've seen today
      if (excludeUserId) {
        const today = new Date().toISOString().split('T')[0];

        // Get question IDs seen today
        const { data: seenToday } = await supabase
          .from('user_question_progress')
          .select('question_id')
          .eq('user_id', excludeUserId)
          .gte('last_seen_at', `${today}T00:00:00.000Z`);

        const seenIds = (seenToday || []).map((s) => s.question_id);

        let query = supabase
          .from('questions')
          .select('*')
          .eq('status', 'published')
          .eq('question_type', 'clinical');

        if (category) {
          query = query.eq('category', category);
        }

        if (seenIds.length > 0) {
          query = query.not('id', 'in', `(${seenIds.join(',')})`);
        }

        query = query.limit(limit);

        const { data, error } = await query;
        if (error) throw error;
        return data as Question[];
      }

      // Default behavior without filtering
      let query = supabase
        .from('questions')
        .select('*')
        .eq('status', 'published')
        .eq('question_type', 'clinical')
        .limit(limit);

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Question[];
    },

    async getById(questionId: string) {
      const { data, error } = await supabase
        .from('questions')
        .select('*')
        .eq('id', questionId)
        .single();
      if (error) throw error;
      return data as Question;
    },

    async getDueForReview(userId: string, limit = 10) {
      const today = localDateString();
      const { data, error } = await supabase
        .from('user_question_progress')
        .select('question_id, questions!inner(*)')
        .eq('user_id', userId)
        .eq('questions.question_type', 'clinical')
        // A question pulled from rotation (e.g. held for physician QC) must not resurface.
        .eq('questions.status', 'published')
        .lte('next_review_date', today)
        .order('next_review_date', { ascending: true }) // most overdue first
        .limit(limit);
      if (error) throw error;
      return data;
    },

    /**
     * Weak-areas session: the learner's own low-accuracy questions first (worst first,
     * ties shuffled), then filled from their weakest category through the regular
     * picker. Published clinical questions only, nothing answered in the last 20 hours,
     * and the category from the Weak Areas page is respected. The old version served
     * held/interview questions, the same 10 every time, and unordered LIMIT fallbacks.
     */
    async getForWeakAreas(userId: string, limit = 10, category?: string): Promise<Question[]> {
      let query = supabase
        .from('user_question_progress')
        .select('times_seen, times_correct, last_seen_at, questions!inner(*)')
        .eq('user_id', userId)
        .gt('times_seen', 0)
        .eq('questions.status', 'published')
        .eq('questions.question_type', 'clinical');
      if (category) query = query.eq('questions.category', category);
      const { data: progress, error } = await query;
      if (error) throw error;

      type Row = { times_seen: number; times_correct: number; last_seen_at: string | null; questions: Question | Question[] };
      const rows = ((progress || []) as Row[]).map((r) => ({
        ...r,
        q: (Array.isArray(r.questions) ? r.questions[0] : r.questions) as Question,
        accuracy: (r.times_correct || 0) / Math.max(r.times_seen || 0, 1),
      })).filter((r) => r.q);

      const cutoff = Date.now() - 20 * 60 * 60 * 1000;
      const weak = rows
        .filter((r) => r.accuracy < 0.7 && (!r.last_seen_at || new Date(r.last_seen_at).getTime() < cutoff))
        .map((r) => ({ ...r, tiebreak: Math.random() }))
        .sort((a, b) => a.accuracy - b.accuracy || a.tiebreak - b.tiebreak)
        .slice(0, limit)
        .map((r) => r.q);
      if (weak.length >= limit) return weak;

      // Fill from the weakest category (or the requested one) via the regular picker.
      let fillCategory = category;
      if (!fillCategory) {
        const byCat: Record<string, { c: number; t: number }> = {};
        rows.forEach((r) => {
          const k = r.q.category;
          byCat[k] = byCat[k] || { c: 0, t: 0 };
          byCat[k].c += r.times_correct || 0;
          byCat[k].t += r.times_seen || 0;
        });
        fillCategory = Object.entries(byCat)
          .filter(([, v]) => v.t >= 3 && v.c / v.t < 0.7)
          .sort((a, b) => a[1].c / a[1].t - b[1].c / b[1].t)[0]?.[0];
      }
      const have = new Set(weak.map((q) => q.id));
      const extra = (await db.questions.selectPractice(userId, limit, fillCategory))
        .filter((q) => !have.has(q.id))
        .slice(0, limit - weak.length);
      return [...weak, ...extra];
    },

    async create(question: Omit<Question, 'id' | 'created_at' | 'updated_at' | 'times_shown' | 'times_correct'>) {
      const { data, error } = await supabase
        .from('questions')
        .insert(question)
        .select()
        .single();
      if (error) throw error;
      return data as Question;
    },

    async update(questionId: string, updates: Partial<Question>) {
      const { data, error } = await supabase
        .from('questions')
        .update(updates)
        .eq('id', questionId)
        .select()
        .single();
      if (error) throw error;
      return data as Question;
    },
  },

  // Practice Sessions
  sessions: {
    async create(session: Omit<PracticeSession, 'id'>) {
      const { data, error } = await supabase
        .from('practice_sessions')
        .insert(session)
        .select()
        .single();
      if (error) throw error;
      return data as PracticeSession;
    },

    async update(sessionId: string, updates: Partial<PracticeSession>) {
      const { data, error } = await supabase
        .from('practice_sessions')
        .update(updates)
        .eq('id', sessionId)
        .select()
        .single();
      if (error) throw error;
      return data as PracticeSession;
    },

    async getRecent(userId: string, limit = 10) {
      const { data, error } = await supabase
        .from('practice_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('started_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as PracticeSession[];
    },
  },

  // User Question Progress (Spaced Repetition)
  progress: {
    async get(userId: string, questionId: string) {
      const { data, error } = await supabase
        .from('user_question_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('question_id', questionId)
        .maybeSingle();
      if (error) throw error;
      return data as UserQuestionProgress | null;
    },

    async upsert(progress: Omit<UserQuestionProgress, 'id'>) {
      const { data, error } = await supabase
        .from('user_question_progress')
        .upsert(progress, {
          onConflict: 'user_id,question_id',
        })
        .select()
        .single();
      if (error) throw error;
      return data as UserQuestionProgress;
    },

    async getBookmarked(userId: string) {
      const { data, error } = await supabase
        .from('user_question_progress')
        .select('question_id, questions(*)')
        .eq('user_id', userId)
        .eq('is_bookmarked', true);
      if (error) throw error;
      return data;
    },
  },

  // Leaderboard
  leaderboard: {
    async getWeekly(limit = 50) {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url, level, xp')
        .order('xp', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data;
    },
  },

  sessions_rpc: {
    /**
     * Close a session on the server (migration 81): recounts it from its answers and awards
     * the perfect-session bonus once. Returns null when the RPC isn't deployed yet.
     */
    async complete(sessionId: string): Promise<{ attempted: number; correct: number; perfect_bonus: number } | null> {
      const { data, error } = await supabase.rpc('complete_session', { p_session_id: sessionId });
      if (error) {
        if (error.code === 'PGRST202' || /complete_session/.test(error.message ?? '')) return null;
        throw error;
      }
      return data as { attempted: number; correct: number; perfect_bonus: number };
    },
    /** Store this browser's time zone so the server's "today" matches the learner's day. */
    async setTimezone(tz: string): Promise<void> {
      const { error } = await supabase.rpc('set_my_timezone', { p_tz: tz });
      if (error && error.code !== 'PGRST202') throw error;
    },
  },

  // Server-side answer submission (tamper-proof)
  answers: {
    async submit(params: {
      userId: string;
      /** null for sessionless answers (interview prep; submit_answer accepts it, migration 27). */
      sessionId: string | null;
      questionId: string;
      selectedOption: number;
      timeSpentMs?: number;
    }) {
      const { data, error } = await supabase.rpc('submit_answer', {
        p_user_id: params.userId,
        p_session_id: params.sessionId,
        p_question_id: params.questionId,
        p_selected_option: params.selectedOption,
        p_time_spent_ms: params.timeSpentMs ?? null,
      });
      if (error) throw error;
      return data as {
        correct: boolean;
        xp_earned: number;
        correct_streak: number;
        new_xp: number;
        new_level: number;
        streak_days: number;
        questions_today: number;
        daily_goal_completed: boolean;
        next_review_date: string;
      };
    },
  },

  // Achievements
  achievements: {
    async getUserAchievements(userId: string) {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', userId);
      if (error) throw error;
      return data;
    },

    /**
     * Server-checked achievement (migration 82): verifies the criteria against the server's
     * own numbers, records it once and awards its XP once. Returns null if not deployed yet.
     */
    async claim(achievementId: string): Promise<{ awarded: boolean; xp?: number; error?: string } | null> {
      const { data, error } = await supabase.rpc('claim_achievement', { p_achievement_id: achievementId });
      if (error) {
        if (error.code === 'PGRST202') return null;
        throw error;
      }
      return data as { awarded: boolean; xp?: number; error?: string };
    },

    async award(userId: string, achievementId: string) {
      const { data, error } = await supabase
        .from('user_achievements')
        .insert({
          user_id: userId,
          achievement_id: achievementId,
          earned_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  },
};

// ============================================
// Real-time Subscriptions
// ============================================

export const realtime = {
  // Subscribe to user updates
  subscribeToUser(userId: string, callback: (payload: unknown) => void) {
    return supabase
      .channel(`user:${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'users',
        filter: `id=eq.${userId}`,
      }, callback)
      .subscribe();
  },

  // Subscribe to leaderboard updates
  subscribeToLeaderboard(callback: (payload: unknown) => void) {
    return supabase
      .channel('leaderboard')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'users',
      }, callback)
      .subscribe();
  },

  // Unsubscribe
  unsubscribe(channel: ReturnType<typeof supabase.channel>) {
    return supabase.removeChannel(channel);
  },
};

export default supabase;
